import React, { useState, useEffect } from 'react';
import IconPicker from './IconPicker';

const HolidayManagementPanel = ({ adminToken, isDarkMode, onSettingsUpdate }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showFullEditModal, setShowFullEditModal] = useState(false);
  const [fullEditHoliday, setFullEditHoliday] = useState(null);
  const [holidaysEnabled, setHolidaysEnabled] = useState(true);
  const [showScheduleSettings, setShowScheduleSettings] = useState(false);
  const [showSendNowModal, setShowSendNowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activePanel, setActivePanel] = useState('holidays');
  const [holidayDisplayDays, setHolidayDisplayDays] = useState(7);
  const [savingDisplayDays, setSavingDisplayDays] = useState(false);
  // Guide tab state
  const [guideSteps, setGuideSteps] = useState([]);
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [editStepForm, setEditStepForm] = useState({});
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [newStep, setNewStep] = useState({ icon: '📌', title: '', description: '', color: 'bg-blue-500', highlight_text: '' });
  const [draggedStepIdx, setDraggedStepIdx] = useState(null);
  const [stepOrderChanged, setStepOrderChanged] = useState(false);
  const [savingStepOrder, setSavingStepOrder] = useState(false);
  const [newHoliday, setNewHoliday] = useState({
    name_en: '',
    name_darija: '',
    name_ar: '',
    icon: '🎉',
    type: 'cultural',
    date_2025: '',
    date_2026: '',
    greeting_darija: '',
    greeting_en: '',
    description_en: '',
    description_darija: '',
    cultural_notes: '',
    duration_days: 1
  });
  const [scheduleSettings, setScheduleSettings] = useState({
    enabled: false,
    notificationTime: '00:00',
    timezone: 'Africa/Casablanca',
    daysBeforeOptions: [1, 3, 7],
    defaultDaysBefore: 7
  });

  // Icon picker state
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [iconPickerTarget, setIconPickerTarget] = useState(null);
  // Page-level icon settings
  const [pageSettings, setPageSettings] = useState(null);

  useEffect(() => {
    loadHolidays();
    loadSettings();
    loadScheduleSettings();
  }, []);

  useEffect(() => {
    if (activePanel === 'guide') loadGuideData();
    if (activePanel === 'settings' && !pageSettings) loadPageSettings();
  }, [activePanel]);

  const loadGuideData = async () => {
    setLoadingGuide(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/holidays/info-steps`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGuideSteps(data.steps || []);
      }
    } catch (err) {
      console.error('Error loading guide steps:', err);
    } finally {
      setLoadingGuide(false);
    }
  };

  const seedGuideDefaults = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/holidays/info-steps/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast(data.message || 'Default guide steps created!', 'success');
        loadGuideData();
      } else {
        showToast('Failed (' + res.status + '): ' + (data.detail || data.message || 'Unknown error'), 'error');
      }
    } catch (err) {
      showToast('Error seeding defaults: ' + err.message, 'error');
    }
  };

  const addGuideStep = async () => {
    if (!newStep.title) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/holidays/info-steps`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newStep)
      });
      if (res.ok) {
        showToast('Step added!', 'success');
        setShowAddStepModal(false);
        setNewStep({ icon: '📌', title: '', description: '', color: 'bg-blue-500', highlight_text: '' });
        loadGuideData();
      }
    } catch (err) {
      showToast('Error adding step: ' + err.message, 'error');
    }
  };

  const updateGuideStep = async (stepId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/holidays/info-steps/${stepId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editStepForm)
      });
      if (res.ok) {
        showToast('Step updated!', 'success');
        setEditingStep(null);
        loadGuideData();
      }
    } catch (err) {
      showToast('Error updating step: ' + err.message, 'error');
    }
  };

  const deleteGuideStep = async (stepId) => {
    if (!window.confirm('Delete this step?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/holidays/info-steps/${stepId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showToast('Step deleted!', 'success');
        loadGuideData();
      }
    } catch (err) {
      showToast('Error deleting step: ' + err.message, 'error');
    }
  };

  const saveStepOrder = async () => {
    setSavingStepOrder(true);
    try {
      const step_ids = guideSteps.map(s => s.id);
      const res = await fetch(`${BACKEND_URL}/api/admin/holidays/info-steps-reorder`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ step_ids })
      });
      if (res.ok) {
        showToast('Step order saved!', 'success');
        setStepOrderChanged(false);
        loadGuideData();
      }
    } catch (err) {
      showToast('Error saving order: ' + err.message, 'error');
    } finally {
      setSavingStepOrder(false);
    }
  };

  const handleStepDragStart = (idx) => {
    setDraggedStepIdx(idx);
  };

  const handleStepDrop = (targetIdx) => {
    if (draggedStepIdx === null || draggedStepIdx === targetIdx) return;
    const updated = [...guideSteps];
    const [moved] = updated.splice(draggedStepIdx, 1);
    updated.splice(targetIdx, 0, moved);
    setGuideSteps(updated);
    setDraggedStepIdx(null);
    setStepOrderChanged(true);
  };

  const colorOptions = [
    { value: 'bg-blue-500', label: 'Blue' },
    { value: 'bg-purple-500', label: 'Purple' },
    { value: 'bg-pink-500', label: 'Pink' },
    { value: 'bg-green-500', label: 'Green' },
    { value: 'bg-yellow-500', label: 'Yellow' },
    { value: 'bg-red-500', label: 'Red' },
    { value: 'bg-orange-500', label: 'Orange' },
    { value: 'bg-teal-500', label: 'Teal' },
  ];

  const loadSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHolidaysEnabled(data.holidaysEnabled !== false);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadScheduleSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/holiday-schedule-settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScheduleSettings(data);
        setHolidayDisplayDays(data.holidayDisplayDays || 7);
      }
    } catch (error) {
      console.error('Error loading schedule settings:', error);
    }
  };

  // Load page icon settings
  const loadPageSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/holidays/page-settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) setPageSettings(await res.json());
    } catch {}
  };

  const savePageSetting = async (fields) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/holidays/page-settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(fields)
      });
      if (res.ok) {
        setPageSettings(prev => ({ ...prev, ...fields }));
        showToast('Saved!', 'success');
      } else {
        showToast('Failed to save', 'error');
      }
    } catch {
      showToast('Failed to save', 'error');
    }
  };

  // Icon picker functions
  const openIconPicker = (target) => {
    setIconPickerTarget(target);
    setIconPickerOpen(true);
  };

  const handleIconSelect = async (url) => {
    setIconPickerOpen(false);
    const t = iconPickerTarget;
    if (!t) return;
    if (t.type === 'holiday') {
      await updateHoliday(t.id, { icon_url: url });
      if (fullEditHoliday && fullEditHoliday.id === t.id) {
        setFullEditHoliday(prev => ({ ...prev, icon_url: url }));
      }
    } else if (t.type === 'step') {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/holidays/info-steps/${t.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ icon_url: url })
        });
        if (res.ok) {
          showToast('Step icon updated!', 'success');
          loadGuideData();
        }
      } catch {}
      if (editStepForm && editingStep === t.id) {
        setEditStepForm(prev => ({ ...prev, icon_url: url }));
      }
    } else if (t.type === 'page') {
      await savePageSetting({ [t.field]: url });
    } else if (t.type === 'newHoliday') {
      setNewHoliday(prev => ({ ...prev, icon_url: url }));
    } else if (t.type === 'newStep') {
      setNewStep(prev => ({ ...prev, icon_url: url }));
    }
  };

  const clearIconUrl = async (target) => {
    if (target.type === 'holiday') {
      await updateHoliday(target.id, { icon_url: '' });
      if (fullEditHoliday && fullEditHoliday.id === target.id) {
        setFullEditHoliday(prev => ({ ...prev, icon_url: '' }));
      }
    } else if (target.type === 'step') {
      try {
        await fetch(`${BACKEND_URL}/api/admin/holidays/info-steps/${target.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ icon_url: '' })
        });
        showToast('Step icon cleared!', 'success');
        loadGuideData();
      } catch {}
    } else if (target.type === 'page') {
      await savePageSetting({ [target.field]: '' });
    } else if (target.type === 'newHoliday') {
      setNewHoliday(prev => ({ ...prev, icon_url: '' }));
    } else if (target.type === 'newStep') {
      setNewStep(prev => ({ ...prev, icon_url: '' }));
    }
  };

  const saveHolidayDisplayDays = async (newDays) => {
    setSavingDisplayDays(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/holiday-schedule-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...scheduleSettings, holidayDisplayDays: newDays })
      });

      if (response.ok) {
        setHolidayDisplayDays(newDays);
        showToast('Holiday display days updated successfully!', 'success');
      } else {
        showToast('Failed to save setting', 'error');
      }
    } catch (error) {
      showToast('Error saving setting: ' + error.message, 'error');
    } finally {
      setSavingDisplayDays(false);
    }
  };

  const toggleHolidaysVisibility = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings/toggle-holidays`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHolidaysEnabled(data.holidaysEnabled);
        showToast(data.message, 'success');
        
        // Refresh global settings
        if (onSettingsUpdate) {
          onSettingsUpdate();
        }
      } else {
        showToast('Failed to toggle holidays visibility', 'error');
      }
    } catch (error) {
      showToast('Error toggling holidays visibility', 'error');
    }
  };

  const loadHolidays = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/holidays`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHolidays(data.holidays || []);
      }
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const createHoliday = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/holidays`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newHoliday)
      });

      if (response.ok) {
        showToast('✅ Holiday created successfully!', 'success');
        loadHolidays();
        setShowAddModal(false);
        // Reset form
        setNewHoliday({
          name_en: '',
          name_darija: '',
          name_ar: '',
          icon: '🎉',
          type: 'cultural',
          date_2025: '',
          date_2026: '',
          greeting_darija: '',
          greeting_en: '',
          description_en: '',
          description_darija: '',
          cultural_notes: '',
          duration_days: 1
        });
      } else {
        showToast('❌ Failed to create holiday', 'error');
      }
    } catch (error) {
      console.error('Error creating holiday:', error);
      showToast('❌ Error creating holiday', 'error');
    }
  };

  const deleteHoliday = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/holidays/${holidayId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (response.ok) {
        showToast('✅ Holiday deleted successfully!', 'success');
        loadHolidays();
      } else {
        showToast('❌ Failed to delete holiday', 'error');
      }
    } catch (error) {
      console.error('Error deleting holiday:', error);
      showToast('❌ Error deleting holiday', 'error');
    }
  };

  const updateHoliday = async (holidayId, updateData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/holidays/${holidayId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        showToast('✅ Holiday updated successfully!', 'success');
        loadHolidays();
        setEditingHoliday(null);
      } else {
        showToast('❌ Failed to update holiday', 'error');
      }
    } catch (error) {
      showToast('❌ Error updating holiday', 'error');
    }
  };

  const toggleAllNotifications = async (enabled) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/holidays/toggle-notifications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled })
      });

      if (response.ok) {
        showToast(`✅ All notifications ${enabled ? 'enabled' : 'disabled'}!`, 'success');
        loadHolidays();
      }
    } catch (error) {
      showToast('❌ Error toggling notifications', 'error');
    }
  };

  const showToast = (message, type) => {
    // Create toast notification element
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 text-white font-semibold ${
      type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500'
    }`;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    // Fade out and remove after 3 seconds
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.5s';
      setTimeout(() => document.body.removeChild(toast), 500);
    }, 3000);
  };

  const startEdit = (holiday) => {
    setEditingHoliday(holiday.id);
    setEditForm({
      date_2024: holiday.date_2024 || '',
      date_2025: holiday.date_2025 || '',
      date_2026: holiday.date_2026 || '',
      is_active: holiday.is_active,
      notification_enabled: holiday.notification_enabled
    });
  };

  const openFullEdit = (holiday) => {
    setFullEditHoliday({
      id: holiday.id,
      name_en: holiday.name_en || '',
      name_ar: holiday.name_ar || '',
      name_darija: holiday.name_darija || '',
      description_en: holiday.description_en || '',
      description_darija: holiday.description_darija || '',
      greeting_en: holiday.greeting_en || '',
      greeting_darija: holiday.greeting_darija || '',
      greeting_ar: holiday.greeting_ar || '',
      cultural_notes: holiday.cultural_notes || '',
      icon: holiday.icon || '',
      icon_url: holiday.icon_url || '',
      duration_days: holiday.duration_days || 1
    });
    setShowFullEditModal(true);
  };

  const saveFullEdit = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/holidays/${fullEditHoliday.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fullEditHoliday)
      });

      if (response.ok) {
        showToast('✅ Holiday content updated successfully!', 'success');
        loadHolidays();
        setShowFullEditModal(false);
        setFullEditHoliday(null);
      } else {
        showToast('❌ Failed to update holiday content', 'error');
      }
    } catch (error) {
      showToast('❌ Error updating holiday content', 'error');
    }
  };

  const saveEdit = () => {
    if (editingHoliday) {
      updateHoliday(editingHoliday, editForm);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'islamic': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'national': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'cultural': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const allNotificationsEnabled = holidays.every(h => h.notification_enabled);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold dark:text-white">Holiday Management</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage Moroccan holidays, dates, and notification settings
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b-2 border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActivePanel('holidays')}
          className={`px-6 py-3 font-semibold rounded-t-lg transition-all ${
            activePanel === 'holidays'
              ? isDarkMode
                ? 'bg-gray-800 text-blue-400 border-b-2 border-blue-400 -mb-[2px]'
                : 'bg-white text-blue-600 border-b-2 border-blue-600 -mb-[2px]'
              : isDarkMode
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          Holidays
        </button>
        <button
          onClick={() => setActivePanel('settings')}
          className={`px-6 py-3 font-semibold rounded-t-lg transition-all ${
            activePanel === 'settings'
              ? isDarkMode
                ? 'bg-gray-800 text-blue-400 border-b-2 border-blue-400 -mb-[2px]'
                : 'bg-white text-blue-600 border-b-2 border-blue-600 -mb-[2px]'
              : isDarkMode
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          Settings
        </button>
        <button
          onClick={() => setActivePanel('guide')}
          className={`px-6 py-3 font-semibold rounded-t-lg transition-all ${
            activePanel === 'guide'
              ? isDarkMode
                ? 'bg-gray-800 text-blue-400 border-b-2 border-blue-400 -mb-[2px]'
                : 'bg-white text-blue-600 border-b-2 border-blue-600 -mb-[2px]'
              : isDarkMode
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          Guide
        </button>
      </div>

      {/* === HOLIDAYS TAB === */}
      {activePanel === 'holidays' && (<>
      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
        >
          Add Holiday
        </button>
        <button
          onClick={() => setShowSendNowModal(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
        >
          Send Notifications Now
        </button>
        <button
          onClick={() => setShowScheduleSettings(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
        >
          Schedule Settings
        </button>
        <button
          onClick={toggleHolidaysVisibility}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            holidaysEnabled
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-600 text-white hover:bg-gray-700'
          }`}
        >
          {holidaysEnabled ? 'Visible' : 'Hidden'}
        </button>
        <button
          onClick={() => loadHolidays()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Reload
        </button>
      </div>

      {/* Global Toggle */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 p-6 rounded-xl border-2 border-orange-200 dark:border-orange-800">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-bold text-orange-900 dark:text-orange-100">
              🔔 Global Holiday Notifications
            </h4>
            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
              Enable or disable all holiday email reminders system-wide
            </p>
          </div>
          <button
            onClick={() => toggleAllNotifications(!allNotificationsEnabled)}
            className={`px-6 py-3 rounded-xl font-bold text-white shadow-lg ${
              allNotificationsEnabled 
                ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                : 'bg-gradient-to-r from-red-500 to-pink-600'
            }`}
          >
            {allNotificationsEnabled ? '✅ All Enabled' : '❌ All Disabled'}
          </button>
        </div>
      </div>

      {/* Holidays Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left">Holiday</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-center">2024 Date</th>
                <th className="px-4 py-3 text-center">2025 Date</th>
                <th className="px-4 py-3 text-center">2026 Date</th>
                <th className="px-4 py-3 text-center">Active</th>
                <th className="px-4 py-3 text-center">Notifications</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((holiday, index) => {
                const isEditing = editingHoliday === holiday.id;
                
                return (
                  <tr
                    key={holiday.id}
                    className={`border-b border-gray-200 dark:border-gray-700 ${
                      index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : ''
                    }`}
                  >
                    {/* Holiday Name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">
                          {holiday.icon_url
                            ? <img src={`${BACKEND_URL}${holiday.icon_url}`} alt="" className="w-8 h-8 object-contain" />
                            : holiday.icon}
                        </span>
                        <div>
                          <div className="font-bold dark:text-white">{holiday.name_en}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{holiday.name_darija}</div>
                        </div>
                      </div>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTypeColor(holiday.type)}`}>
                        {holiday.type}
                      </span>
                    </td>

                    {/* Dates */}
                    {['2024', '2025', '2026'].map(year => (
                      <td key={year} className="px-4 py-3 text-center">
                        {isEditing ? (
                          <input
                            type="date"
                            value={editForm[`date_${year}`] || ''}
                            onChange={(e) => setEditForm({...editForm, [`date_${year}`]: e.target.value})}
                            className="px-2 py-1 border rounded dark:bg-gray-700 dark:text-white text-sm w-full"
                          />
                        ) : (
                          <span className="text-sm dark:text-gray-300">
                            {holiday[`date_${year}`] 
                              ? new Date(holiday[`date_${year}`] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : '-'}
                          </span>
                        )}
                      </td>
                    ))}

                    {/* Active Status */}
                    <td className="px-4 py-3 text-center">
                      {isEditing ? (
                        <input
                          type="checkbox"
                          checked={editForm.is_active}
                          onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                          className="w-5 h-5"
                        />
                      ) : (
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          holiday.is_active 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {holiday.is_active ? '✅' : '❌'}
                        </span>
                      )}
                    </td>

                    {/* Notifications */}
                    <td className="px-4 py-3 text-center">
                      {isEditing ? (
                        <input
                          type="checkbox"
                          checked={editForm.notification_enabled}
                          onChange={(e) => setEditForm({...editForm, notification_enabled: e.target.checked})}
                          className="w-5 h-5"
                        />
                      ) : (
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          holiday.notification_enabled 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {holiday.notification_enabled ? '🔔' : '🔕'}
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-center">
                      {isEditing ? (
                        <div className="flex gap-2 justify-center flex-wrap">
                          <button
                            onClick={saveEdit}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          >
                            💾 Save
                          </button>
                          <button
                            onClick={() => setEditingHoliday(null)}
                            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                          >
                            ❌ Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2 justify-center flex-wrap">
                          <button
                            onClick={() => startEdit(holiday)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                            title="Edit dates and status"
                          >
                            📅 Dates
                          </button>
                          <button
                            onClick={() => openFullEdit(holiday)}
                            className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                            title="Edit all text content"
                          >
                            ✏️ Content
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2">Holiday Management Tips</h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• <strong>Active:</strong> Holiday appears on holidays page</li>
          <li>• <strong>Notifications:</strong> Send email reminders {holidayDisplayDays} {holidayDisplayDays === 1 ? 'day' : 'days'} before</li>
          <li>• <strong>Islamic dates:</strong> Based on lunar calendar - may vary by 1-2 days</li>
          <li>• <strong>Dates button:</strong> Quick edit dates and status</li>
          <li>• <strong>Content button:</strong> Edit ALL text (names, descriptions, greetings, cultural notes)</li>
          <li>• <strong>Global toggle:</strong> Disable all notifications at once if needed</li>
        </ul>
      </div>
      </>)}

      {/* === SETTINGS TAB === */}
      {activePanel === 'settings' && (
        <div className="space-y-6">
          {/* Holiday Display Days Setting */}
          <div className={`rounded-xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h4 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Notification Display Window
            </h4>
            <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Control how many days before a holiday the notification appears in the sidebar (next to weather).
              This affects the bouncing notification icon users see.
            </p>

            <div className="max-w-lg">
              <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Show notification this many days before:
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="1"
                  max="30"
                  value={holidayDisplayDays}
                  onChange={(e) => setHolidayDisplayDays(parseInt(e.target.value))}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className={`flex items-baseline gap-1 min-w-[80px] justify-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <span className="text-3xl font-bold">{holidayDisplayDays}</span>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {holidayDisplayDays === 1 ? 'day' : 'days'}
                  </span>
                </div>
              </div>
              <div className={`flex justify-between text-xs mt-1 px-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <span>1 day</span>
                <span>1 week</span>
                <span>2 weeks</span>
                <span>1 month</span>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={() => saveHolidayDisplayDays(holidayDisplayDays)}
                disabled={savingDisplayDays}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
              >
                {savingDisplayDays ? 'Saving...' : 'Save Setting'}
              </button>
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Currently: {holidayDisplayDays} {holidayDisplayDays === 1 ? 'day' : 'days'} before holiday
              </span>
            </div>
          </div>

          {/* Quick Preset Buttons */}
          <div className={`rounded-xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h4 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Quick Presets
            </h4>
            <div className="flex gap-3 flex-wrap">
              {[
                { days: 3, label: '3 Days' },
                { days: 7, label: '1 Week' },
                { days: 14, label: '2 Weeks' },
                { days: 21, label: '3 Weeks' },
                { days: 30, label: '1 Month' }
              ].map(preset => (
                <button
                  key={preset.days}
                  onClick={() => {
                    setHolidayDisplayDays(preset.days);
                    saveHolidayDisplayDays(preset.days);
                  }}
                  disabled={savingDisplayDays}
                  className={`px-5 py-2.5 rounded-lg font-semibold transition ${
                    holidayDisplayDays === preset.days
                      ? 'bg-blue-600 text-white shadow-lg'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* How It Works Info Box */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
            <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>How it works</h4>
            <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              <li>• The sidebar notification (next to weather) appears <strong>{holidayDisplayDays} {holidayDisplayDays === 1 ? 'day' : 'days'}</strong> before each holiday</li>
              <li>• Users see the bouncing holiday icon when a holiday is within this window</li>
              <li>• The Holidays page always shows upcoming holidays within at least 30 days</li>
              <li>• This setting is saved to the database and applies globally for all users</li>
            </ul>
          </div>

          {/* Page Icons */}
          <div className={`rounded-xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h4 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Page Icons
            </h4>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Pick custom icons for the Holidays page. Leave empty for default emoji.
            </p>
            {pageSettings ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: 'Header', field: 'header_icon_url', emoji: '\ud83c\uddf2\ud83c\udde6' },
                  { label: 'Islamic Type', field: 'type_islamic_icon_url', emoji: '\u262a\ufe0f' },
                  { label: 'National Type', field: 'type_national_icon_url', emoji: '\ud83c\uddf2\ud83c\udde6' },
                  { label: 'Cultural Type', field: 'type_cultural_icon_url', emoji: '\ud83c\udfa8' },
                  { label: 'Default Type', field: 'type_default_icon_url', emoji: '\ud83d\udcc5' },
                  { label: 'Upcoming Banner', field: 'upcoming_main_icon_url', emoji: '\ud83c\udf89' },
                  { label: 'Upcoming Star', field: 'upcoming_star_icon_url', emoji: '\ud83c\udf1f' },
                  { label: 'Upcoming Heart', field: 'upcoming_heart_icon_url', emoji: '\u2764\ufe0f' },
                  { label: 'Floating Party', field: 'floating_party_icon_url', emoji: '\ud83c\udf8a' },
                  { label: 'Floating Sparkle', field: 'floating_sparkle_icon_url', emoji: '\u2728' },
                  { label: 'Floating Balloon', field: 'floating_balloon_icon_url', emoji: '\ud83c\udf88' },
                  { label: 'Floating Star', field: 'floating_star_icon_url', emoji: '\u2b50' },
                  { label: 'Greeting Section', field: 'greeting_icon_url', emoji: '\ud83d\udc4b' },
                  { label: 'Cultural Insight', field: 'cultural_insight_icon_url', emoji: '\ud83d\udca1' },
                  { label: 'Duration', field: 'duration_icon_url', emoji: '\ud83d\udcc5' },
                  { label: 'Info Section', field: 'info_section_icon_url', emoji: '\ud83d\udce7' },
                  { label: 'Filter All', field: 'filter_all_icon_url', emoji: '\ud83c\udf8a' },
                  { label: 'Sidebar Notification', field: 'notification_icon_url', emoji: '\ud83c\udf89' },
                  { label: 'Card Party', field: 'highlight_party_icon_url', emoji: '\ud83c\udf89' },
                  { label: 'Card Sparkle', field: 'highlight_sparkle_icon_url', emoji: '\u2728' },
                  { label: 'Card Confetti', field: 'highlight_confetti_icon_url', emoji: '\ud83c\udf8a' },
                  { label: 'No Results', field: 'no_results_icon_url', emoji: '\ud83d\udd0d' },
                  { label: 'Date Sparkle', field: 'date_sparkle_icon_url', emoji: '\ud83d\udcab' },
                ].map((ic) => (
                  <div key={ic.field} className={`flex items-center gap-3 p-2 rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                      {pageSettings[ic.field]
                        ? <img src={`${BACKEND_URL}${pageSettings[ic.field]}`} alt="" className="w-8 h-8 object-contain" />
                        : <span className="text-xl">{ic.emoji}</span>}
                    </div>
                    <span className={`flex-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ic.label}</span>
                    <button
                      onClick={() => openIconPicker({ type: 'page', field: ic.field })}
                      className="px-2 py-1 text-xs rounded font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-700 dark:text-white dark:hover:bg-purple-600"
                    >Pick</button>
                    {pageSettings[ic.field] && (
                      <button
                        onClick={() => clearIconUrl({ type: 'page', field: ic.field })}
                        className="px-2 py-1 text-xs rounded font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-700 dark:text-white dark:hover:bg-red-600"
                      >Clear</button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === GUIDE TAB === */}
      {activePanel === 'guide' && (
        <div className="space-y-6">
          {loadingGuide ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          ) : guideSteps.length === 0 ? (
            <div className={`rounded-xl p-8 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <p className={`text-lg mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                No guide steps yet. Seed the defaults to get started.
              </p>
              <button
                onClick={seedGuideDefaults}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                Seed Default Steps
              </button>
            </div>
          ) : (
            <>
              {/* Action bar */}
              <div className="flex gap-3 flex-wrap items-center">
                <button
                  onClick={() => setShowAddStepModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                >
                  + Add Step
                </button>
                <button
                  onClick={() => loadGuideData()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Reload
                </button>
                {stepOrderChanged && (
                  <button
                    onClick={saveStepOrder}
                    disabled={savingStepOrder}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-semibold disabled:opacity-50"
                  >
                    {savingStepOrder ? 'Saving...' : 'Save Order'}
                  </button>
                )}
              </div>

              {stepOrderChanged && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg px-4 py-2 text-sm text-yellow-800 dark:text-yellow-200">
                  Order changed - click "Save Order" to persist.
                </div>
              )}

              {/* Steps list */}
              <div className="space-y-3">
                {guideSteps.map((step, idx) => (
                  <div
                    key={step.id}
                    draggable
                    onDragStart={() => handleStepDragStart(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleStepDrop(idx)}
                    className={`rounded-xl shadow p-4 border-l-4 ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} cursor-grab`}
                    style={{ borderLeftColor: step.color?.replace('bg-', '').includes('blue') ? '#3B82F6' : step.color?.includes('purple') ? '#8B5CF6' : step.color?.includes('pink') ? '#EC4899' : step.color?.includes('green') ? '#22C55E' : step.color?.includes('yellow') ? '#EAB308' : step.color?.includes('red') ? '#EF4444' : step.color?.includes('orange') ? '#F97316' : step.color?.includes('teal') ? '#14B8A6' : '#6B7280' }}
                  >
                    {editingStep === step.id ? (
                      /* Editing mode */
                      <div className="space-y-3">
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Icon</label>
                            <input
                              type="text"
                              value={editStepForm.icon || ''}
                              onChange={(e) => setEditStepForm({...editStepForm, icon: e.target.value})}
                              className={`w-full px-3 py-2 border rounded-lg text-center text-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                            />
                            <div className="flex items-center gap-1 mt-1">
                              {editStepForm.icon_url && <img src={`${BACKEND_URL}${editStepForm.icon_url}`} alt="" className="w-5 h-5 object-contain" />}
                              <button type="button" onClick={() => openIconPicker({ type: 'step', id: step.id })} className="px-2 py-0.5 text-[10px] rounded font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-700 dark:text-white dark:hover:bg-purple-600">Pick</button>
                              {editStepForm.icon_url && <button type="button" onClick={() => clearIconUrl({ type: 'step', id: step.id })} className="px-2 py-0.5 text-[10px] rounded font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-700 dark:text-white dark:hover:bg-red-600">Clear</button>}
                            </div>
                          </div>
                          <div className="col-span-3">
                            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Title</label>
                            <input
                              type="text"
                              value={editStepForm.title || ''}
                              onChange={(e) => setEditStepForm({...editStepForm, title: e.target.value})}
                              className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                          <textarea
                            value={editStepForm.description || ''}
                            onChange={(e) => setEditStepForm({...editStepForm, description: e.target.value})}
                            rows={2}
                            className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Color</label>
                            <select
                              value={editStepForm.color || 'bg-blue-500'}
                              onChange={(e) => setEditStepForm({...editStepForm, color: e.target.value})}
                              className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                            >
                              {colorOptions.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Highlight Text</label>
                            <input
                              type="text"
                              value={editStepForm.highlight_text || ''}
                              onChange={(e) => setEditStepForm({...editStepForm, highlight_text: e.target.value})}
                              className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                              placeholder="e.g. 7 days before"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => updateGuideStep(step.id)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold">Save</button>
                          <button onClick={() => setEditingStep(null)} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      /* Display mode */
                      <div className="flex items-start gap-4">
                        <span className="text-3xl flex-shrink-0 cursor-grab">
                          {step.icon_url
                            ? <img src={`${BACKEND_URL}${step.icon_url}`} alt="" className="w-8 h-8 object-contain" />
                            : step.icon}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{step.title}</span>
                            {step.highlight_text && (
                              <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded-full font-semibold">
                                {step.highlight_text}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{step.description}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => { setEditingStep(step.id); setEditStepForm({ icon: step.icon, icon_url: step.icon_url || '', title: step.title, description: step.description, color: step.color, highlight_text: step.highlight_text }); }}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteGuideStep(step.id)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Info box */}
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>Guide Management</h4>
                <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  <li>• These steps appear in the "How Holiday Notifications Work" section on the public Holidays page</li>
                  <li>• Drag to reorder steps, then click "Save Order" to persist</li>
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {/* Add Step Modal */}
      {showAddStepModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-lg rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Add Guide Step</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Icon</label>
                  <input
                    type="text"
                    value={newStep.icon}
                    onChange={(e) => setNewStep({...newStep, icon: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg text-center text-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                  <div className="flex items-center gap-1 mt-1">
                    {newStep.icon_url && <img src={`${BACKEND_URL}${newStep.icon_url}`} alt="" className="w-5 h-5 object-contain" />}
                    <button type="button" onClick={() => openIconPicker({ type: 'newStep' })} className="px-2 py-0.5 text-[10px] rounded font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-700 dark:text-white dark:hover:bg-purple-600">Pick</button>
                    {newStep.icon_url && <button type="button" onClick={() => clearIconUrl({ type: 'newStep' })} className="px-2 py-0.5 text-[10px] rounded font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-700 dark:text-white dark:hover:bg-red-600">Clear</button>}
                  </div>
                </div>
                <div className="col-span-3">
                  <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Title *</label>
                  <input
                    type="text"
                    value={newStep.title}
                    onChange={(e) => setNewStep({...newStep, title: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Step title"
                  />
                </div>
              </div>
              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                <textarea
                  value={newStep.description}
                  onChange={(e) => setNewStep({...newStep, description: e.target.value})}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Step description..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Color</label>
                  <select
                    value={newStep.color}
                    onChange={(e) => setNewStep({...newStep, color: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  >
                    {colorOptions.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Highlight Text</label>
                  <input
                    type="text"
                    value={newStep.highlight_text}
                    onChange={(e) => setNewStep({...newStep, highlight_text: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="e.g. flashing icon"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddStepModal(false)} className={`flex-1 px-4 py-2 border rounded-lg font-semibold ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
              <button onClick={addGuideStep} disabled={!newStep.title} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50">Add Step</button>
            </div>
          </div>
        </div>
      )}

      {/* Full Edit Modal */}
      {showFullEditModal && fullEditHoliday && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 text-white sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold">✏️ Edit Holiday Content</h3>
                <button
                  onClick={() => {
                    setShowFullEditModal(false);
                    setFullEditHoliday(null);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition"
                >
                  ❌
                </button>
              </div>
              <p className="text-sm text-white text-opacity-90 mt-2">
                Edit all text content for this holiday - names, descriptions, greetings, and cultural notes
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Icon & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-2 dark:text-white">
                    Icon Emoji
                  </label>
                  <input
                    type="text"
                    value={fullEditHoliday.icon}
                    onChange={(e) => setFullEditHoliday({...fullEditHoliday, icon: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-2xl text-center"
                    placeholder="🎉"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    {fullEditHoliday.icon_url && (
                      <img src={`${BACKEND_URL}${fullEditHoliday.icon_url}`} alt="" className="w-8 h-8 object-contain" />
                    )}
                    <button
                      type="button"
                      onClick={() => openIconPicker({ type: 'holiday', id: fullEditHoliday.id })}
                      className="px-3 py-1 text-xs rounded font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-700 dark:text-white dark:hover:bg-purple-600"
                    >Pick Icon</button>
                    {fullEditHoliday.icon_url && (
                      <button
                        type="button"
                        onClick={() => clearIconUrl({ type: 'holiday', id: fullEditHoliday.id })}
                        className="px-3 py-1 text-xs rounded font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-700 dark:text-white dark:hover:bg-red-600"
                      >Clear</button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2 dark:text-white">
                    Duration (Days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={fullEditHoliday.duration_days}
                    onChange={(e) => setFullEditHoliday({...fullEditHoliday, duration_days: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Names Section */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-2 border-blue-200 dark:border-blue-700">
                <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                  <span>📝</span> Holiday Names
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold mb-1 dark:text-white">
                      English Name
                    </label>
                    <input
                      type="text"
                      value={fullEditHoliday.name_en}
                      onChange={(e) => setFullEditHoliday({...fullEditHoliday, name_en: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Eid al-Fitr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 dark:text-white">
                      Arabic Name
                    </label>
                    <input
                      type="text"
                      value={fullEditHoliday.name_ar}
                      onChange={(e) => setFullEditHoliday({...fullEditHoliday, name_ar: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-right"
                      placeholder="e.g., عيد الفطر"
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 dark:text-white">
                      Darija Name
                    </label>
                    <input
                      type="text"
                      value={fullEditHoliday.name_darija}
                      onChange={(e) => setFullEditHoliday({...fullEditHoliday, name_darija: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 3id l-Fitr"
                    />
                  </div>
                </div>
              </div>

              {/* Descriptions Section */}
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border-2 border-green-200 dark:border-green-700">
                <h4 className="font-bold text-green-900 dark:text-green-100 mb-3 flex items-center gap-2">
                  <span>📖</span> Descriptions
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold mb-1 dark:text-white">
                      English Description
                    </label>
                    <textarea
                      value={fullEditHoliday.description_en}
                      onChange={(e) => setFullEditHoliday({...fullEditHoliday, description_en: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                      rows="2"
                      placeholder="e.g., Festival of Breaking the Fast - Celebrates the end of Ramadan"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 dark:text-white">
                      Darija Description
                    </label>
                    <textarea
                      value={fullEditHoliday.description_darija}
                      onChange={(e) => setFullEditHoliday({...fullEditHoliday, description_darija: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                      rows="2"
                      placeholder="e.g., 3id dyal l-ftour - ka n7atflo b nhar akhir f Ramadan"
                    />
                  </div>
                </div>
              </div>

              {/* Greetings Section */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border-2 border-yellow-200 dark:border-yellow-700">
                <h4 className="font-bold text-yellow-900 dark:text-yellow-100 mb-3 flex items-center gap-2">
                  <span>👋</span> Traditional Greetings
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold mb-1 dark:text-white">
                      English Greeting
                    </label>
                    <input
                      type="text"
                      value={fullEditHoliday.greeting_en}
                      onChange={(e) => setFullEditHoliday({...fullEditHoliday, greeting_en: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Eid Mubarak!"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 dark:text-white">
                      Arabic Greeting
                    </label>
                    <input
                      type="text"
                      value={fullEditHoliday.greeting_ar}
                      onChange={(e) => setFullEditHoliday({...fullEditHoliday, greeting_ar: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-right"
                      placeholder="e.g., عيد مبارك!"
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1 dark:text-white">
                      Darija Greeting
                    </label>
                    <input
                      type="text"
                      value={fullEditHoliday.greeting_darija}
                      onChange={(e) => setFullEditHoliday({...fullEditHoliday, greeting_darija: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 3id Mubarak!"
                    />
                  </div>
                </div>
              </div>

              {/* Cultural Notes Section */}
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border-2 border-purple-200 dark:border-purple-700">
                <h4 className="font-bold text-purple-900 dark:text-purple-100 mb-3 flex items-center gap-2">
                  <span>💡</span> Cultural Insight
                </h4>
                <textarea
                  value={fullEditHoliday.cultural_notes}
                  onChange={(e) => setFullEditHoliday({...fullEditHoliday, cultural_notes: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  rows="3"
                  placeholder="e.g., Family gatherings, new clothes, special sweets like chebakia and sellou"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 dark:bg-gray-900 p-6 flex gap-3 justify-end sticky bottom-0 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowFullEditModal(false);
                  setFullEditHoliday(null);
                }}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
              >
                ❌ Cancel
              </button>
              <button
                onClick={saveFullEdit}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 font-semibold shadow-lg"
              >
                💾 Save All Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Notifications Now Modal */}
      {showSendNowModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              📧 Send Holiday Notifications Now
            </h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              This will check for holidays coming up in <strong>{holidayDisplayDays} {holidayDisplayDays === 1 ? 'day' : 'days'}</strong> and send email reminders to all users with holiday notifications enabled.
            </p>
            <div className={`p-3 rounded-lg mb-6 ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                ℹ️ <strong>Note:</strong> Emails will be sent from DarijaPro &lt;noreply@darijapro.com&gt;
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSendNowModal(false)}
                className={`flex-1 px-4 py-2 border rounded-lg font-semibold ${
                  isDarkMode 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/holidays/send-notifications`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${adminToken}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      showToast(`✅ Sent ${data.sent_count} emails for ${data.upcoming_holidays} upcoming holidays`, 'success');
                      setShowSendNowModal(false);
                    } else {
                      const error = await response.json();
                      showToast('❌ Failed: ' + (error.detail || 'Unknown error'), 'error');
                    }
                  } catch (error) {
                    showToast('❌ Error: ' + error.message, 'error');
                  }
                }}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
              >
                Send Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Settings Modal */}
      {showScheduleSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-lg p-6 max-w-2xl w-full my-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  ⏰ Automatic Notification Schedule
                </h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Configure when and how holiday notifications are sent
                </p>
              </div>
              <button
                onClick={() => setShowScheduleSettings(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Automation */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleSettings.enabled}
                    onChange={(e) => setScheduleSettings({...scheduleSettings, enabled: e.target.checked})}
                    className="w-5 h-5 rounded"
                  />
                  <div>
                    <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      🤖 Enable Automatic Notifications
                    </span>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      System will check daily and send notifications automatically
                    </p>
                  </div>
                </label>
              </div>

              {/* Notification Time */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  ⏰ Send Notifications At:
                </label>
                <input
                  type="time"
                  value={scheduleSettings.notificationTime}
                  onChange={(e) => setScheduleSettings({...scheduleSettings, notificationTime: e.target.value})}
                  className={`w-full px-4 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Moroccan time (Africa/Casablanca timezone) - Recommended: 12:00 AM (midnight)
                </p>
              </div>

              {/* Days Before Options */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  📅 Send Notifications (Days Before Holiday):
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[1, 3, 5, 7, 10, 14].map(days => (
                    <label key={days} className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer ${
                      scheduleSettings.daysBeforeOptions.includes(days)
                        ? isDarkMode ? 'bg-blue-900/50 border-2 border-blue-600' : 'bg-blue-50 border-2 border-blue-500'
                        : isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-300'
                    }`}>
                      <input
                        type="checkbox"
                        checked={scheduleSettings.daysBeforeOptions.includes(days)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setScheduleSettings({
                              ...scheduleSettings,
                              daysBeforeOptions: [...scheduleSettings.daysBeforeOptions, days].sort((a,b) => a-b)
                            });
                          } else {
                            setScheduleSettings({
                              ...scheduleSettings,
                              daysBeforeOptions: scheduleSettings.daysBeforeOptions.filter(d => d !== days)
                            });
                          }
                        }}
                        className="w-4 h-4 rounded"
                      />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {days} {days === 1 ? 'day' : 'days'} before
                      </span>
                    </label>
                  ))}
                </div>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  💡 Select multiple options to send reminders at different intervals
                </p>
              </div>

              {/* Default Days Before */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  🎯 Default Notification Period:
                </label>
                <select
                  value={scheduleSettings.defaultDaysBefore}
                  onChange={(e) => setScheduleSettings({...scheduleSettings, defaultDaysBefore: parseInt(e.target.value)})}
                  className={`w-full px-4 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value={1}>1 day before</option>
                  <option value={3}>3 days before</option>
                  <option value={7}>7 days before (recommended)</option>
                  <option value={10}>10 days before</option>
                  <option value={14}>14 days before</option>
                </select>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Used for "Send Notifications Now" button and manual triggers
                </p>
              </div>

              {/* Preview Box */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  📋 Current Configuration:
                </p>
                <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                  <li>✅ Status: {scheduleSettings.enabled ? <span className="font-bold text-green-500">ENABLED</span> : <span className="font-bold text-gray-500">DISABLED</span>}</li>
                  <li>⏰ Daily check at: <strong>{scheduleSettings.notificationTime}</strong> (Moroccan time)</li>
                  <li>📅 Send notifications: <strong>{scheduleSettings.daysBeforeOptions.join(', ')} days before</strong> holidays</li>
                  <li>🎯 Default period: <strong>{scheduleSettings.defaultDaysBefore} days</strong></li>
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowScheduleSettings(false)}
                className={`flex-1 px-4 py-2 border rounded-lg font-semibold ${
                  isDarkMode 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(`${BACKEND_URL}/api/admin/holiday-schedule-settings`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${adminToken}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ ...scheduleSettings, holidayDisplayDays })
                    });
                    
                    if (response.ok) {
                      showToast('✅ Schedule settings saved to database!', 'success');
                      setShowScheduleSettings(false);
                    } else {
                      showToast('❌ Failed to save settings', 'error');
                    }
                  } catch (error) {
                    showToast('❌ Error: ' + error.message, 'error');
                  }
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                💾 Save Schedule Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Holiday Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  ➕ Add New Holiday
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {/* Basic Info Row */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Icon
                    </label>
                    <input
                      type="text"
                      value={newHoliday.icon}
                      onChange={(e) => setNewHoliday({...newHoliday, icon: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg text-center text-2xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="🎉"
                    />
                    <div className="flex items-center gap-1 mt-1">
                      {newHoliday.icon_url && <img src={`${BACKEND_URL}${newHoliday.icon_url}`} alt="" className="w-6 h-6 object-contain" />}
                      <button type="button" onClick={() => openIconPicker({ type: 'newHoliday' })} className="px-2 py-0.5 text-[10px] rounded font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-700 dark:text-white dark:hover:bg-purple-600">Pick</button>
                      {newHoliday.icon_url && <button type="button" onClick={() => clearIconUrl({ type: 'newHoliday' })} className="px-2 py-0.5 text-[10px] rounded font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-700 dark:text-white dark:hover:bg-red-600">Clear</button>}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      English Name *
                    </label>
                    <input
                      type="text"
                      value={newHoliday.name_en}
                      onChange={(e) => setNewHoliday({...newHoliday, name_en: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="e.g., Eid al-Fitr"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Type
                    </label>
                    <select
                      value={newHoliday.type}
                      onChange={(e) => setNewHoliday({...newHoliday, type: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    >
                      <option value="islamic">☪️ Islamic</option>
                      <option value="national">🇲🇦 National</option>
                      <option value="cultural">🎨 Cultural</option>
                    </select>
                  </div>
                </div>

                {/* Names Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Darija Name
                    </label>
                    <input
                      type="text"
                      value={newHoliday.name_darija}
                      onChange={(e) => setNewHoliday({...newHoliday, name_darija: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="e.g., 3id Sghir"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Arabic Name
                    </label>
                    <input
                      type="text"
                      value={newHoliday.name_ar}
                      onChange={(e) => setNewHoliday({...newHoliday, name_ar: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg text-right ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="e.g., عيد الفطر"
                      dir="rtl"
                    />
                  </div>
                </div>

                {/* Dates Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      📅 2025 Date
                    </label>
                    <input
                      type="date"
                      value={newHoliday.date_2025}
                      onChange={(e) => setNewHoliday({...newHoliday, date_2025: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      📅 2026 Date *
                    </label>
                    <input
                      type="date"
                      value={newHoliday.date_2026}
                      onChange={(e) => setNewHoliday({...newHoliday, date_2026: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Duration (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={newHoliday.duration_days}
                      onChange={(e) => setNewHoliday({...newHoliday, duration_days: parseInt(e.target.value) || 1})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                </div>

                {/* Greetings Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      👋 Greeting (Darija) *
                    </label>
                    <input
                      type="text"
                      value={newHoliday.greeting_darija}
                      onChange={(e) => setNewHoliday({...newHoliday, greeting_darija: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="e.g., 3id Mubarak!"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      👋 Greeting (English) *
                    </label>
                    <input
                      type="text"
                      value={newHoliday.greeting_en}
                      onChange={(e) => setNewHoliday({...newHoliday, greeting_en: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      placeholder="e.g., Blessed Eid!"
                    />
                  </div>
                </div>

                {/* Descriptions */}
                <div>
                  <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    📝 Description (English) *
                  </label>
                  <textarea
                    value={newHoliday.description_en}
                    onChange={(e) => setNewHoliday({...newHoliday, description_en: e.target.value})}
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Brief description of the holiday..."
                  />
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    📝 Description (Darija)
                  </label>
                  <textarea
                    value={newHoliday.description_darija}
                    onChange={(e) => setNewHoliday({...newHoliday, description_darija: e.target.value})}
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Darija description..."
                  />
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    💡 Cultural Notes
                  </label>
                  <textarea
                    value={newHoliday.cultural_notes}
                    onChange={(e) => setNewHoliday({...newHoliday, cultural_notes: e.target.value})}
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Cultural significance, traditions, etc..."
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className={`flex-1 px-4 py-2 border rounded-lg font-semibold ${
                    isDarkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={createHoliday}
                  disabled={!newHoliday.name_en || !newHoliday.greeting_darija || !newHoliday.greeting_en || !newHoliday.description_en}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ➕ Create Holiday
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Icon Picker Modal */}
      {iconPickerOpen && (
        <IconPicker
          API={`${BACKEND_URL}/api`}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          onClose={() => setIconPickerOpen(false)}
          onPick={(url) => handleIconSelect(url)}
        />
      )}
    </div>
  );
};

export default HolidayManagementPanel;
